import {
	canonicalDataSourceMetaKey,
	DATA_SOURCE_ICON_META,
} from '@jepto/shared/utils/data-source-icon-meta.util';

import { schemaFieldDisplayTitle } from '../components/editors/shared/data-tab/data-tab-dimension.util';
import {
	isSchemaTimeDimensionField,
	timeDimensionDisplayLabelFallback,
} from '../components/editors/shared/data-tab/chart-data-tab-axis.util';
import type {
	AnalyticsSchemaField,
	AnalyticsSchemaSource,
} from '../models/analytics-schema.model';
import { schemaFieldDisplayLabel } from '../models/analytics-schema.model';
import {
	humanizeTableColumnHeader,
	isHumanReadableHeaderLabel,
} from './table-column-header.util';
import { TABLE_EMPTY_CELL_LABEL } from './table-grid.constants';

export interface TableLabelSchemaContext {
	dimensions?: AnalyticsSchemaField[];
	metrics?: AnalyticsSchemaField[];
	sources?: AnalyticsSchemaSource[];
}

function schemaFields(ctx?: TableLabelSchemaContext): AnalyticsSchemaField[] {
	return [...(ctx?.dimensions ?? []), ...(ctx?.metrics ?? [])];
}

function sourceLabelForKey(
	key: string,
	sources?: AnalyticsSchemaSource[]
): string | undefined {
	const fromApi = sources?.find(s => s.type === key)?.label?.trim();
	if (fromApi) {
		return fromApi;
	}
	const mk = canonicalDataSourceMetaKey(key);
	return mk ? DATA_SOURCE_ICON_META[mk].label : undefined;
}

export function resolveTableColumnHeaderLabel(params: {
	columnId: string;
	fieldName?: string;
	cardLabel?: string;
	headerName?: string;
	schema?: TableLabelSchemaContext;
}): string {
	const fieldName = params.fieldName?.trim();
	const schemaFieldsList = schemaFields(params.schema);

	if (fieldName && schemaFieldsList.length > 0) {
		const fromSchema = schemaFieldDisplayTitle(
			fieldName,
			schemaFieldsList,
			schemaFieldDisplayLabel,
			timeDimensionDisplayLabelFallback
		);
		if (
			fromSchema &&
			fromSchema !== fieldName &&
			isHumanReadableHeaderLabel(fromSchema)
		) {
			return fromSchema;
		}
	}

	const cardLabel = params.cardLabel?.trim();
	if (cardLabel && isHumanReadableHeaderLabel(cardLabel)) {
		return cardLabel;
	}

	const headerName = params.headerName?.trim();
	if (
		headerName &&
		headerName !== params.columnId &&
		isHumanReadableHeaderLabel(headerName)
	) {
		return headerName;
	}

	if (fieldName && isSchemaTimeDimensionField({ name: fieldName })) {
		const timeFallback = timeDimensionDisplayLabelFallback(fieldName);
		if (timeFallback && timeFallback !== fieldName) {
			return timeFallback;
		}
	}

	return humanizeTableColumnHeader(
		fieldName || headerName || params.columnId
	);
}

export function formatTableCellDisplayValue(
	value: unknown,
	options?: { sources?: AnalyticsSchemaSource[] }
): string {
	if (value == null || value === '') {
		return TABLE_EMPTY_CELL_LABEL;
	}
	if (typeof value !== 'string') {
		return String(value);
	}
	const trimmed = value.trim();
	const connectorLabel = sourceLabelForKey(trimmed, options?.sources);
	return connectorLabel ?? trimmed;
}

export function normalizeTechnicalHeaderFormat(
	headerFormat: string,
	resolve: (technical: string) => string
): string {
	const trimmed = headerFormat.trim();
	if (!trimmed || isHumanReadableHeaderLabel(trimmed)) {
		return trimmed;
	}
	const resolved = resolve(trimmed);
	if (resolved !== trimmed && isHumanReadableHeaderLabel(resolved)) {
		return resolved;
	}
	return humanizeTableColumnHeader(trimmed);
}

/** Grid Pro header options with a resolved human-readable label. */
export function tableGridColumnHeaderOptions(
	label: string
): Record<string, unknown> {
	return {
		format: label,
		formatter: function tableColumnHeaderFormatter() {
			return label;
		},
	};
}

function tableGridColumnHeaderFormat(
	column: Record<string, unknown>,
	fallback = ''
): string {
	const header = column['header'];
	if (typeof header === 'string') {
		return header;
	}
	if (
		header &&
		typeof header === 'object' &&
		typeof (header as { format?: string }).format === 'string'
	) {
		return (header as { format: string }).format;
	}
	return fallback;
}

export function normalizeTableGridColumnHeaderLabels(
	columns: Array<Record<string, unknown>>,
	context: {
		fieldNameByColumnId?: Map<string, string>;
		headerLabelsById?: Map<string, string>;
		schema?: TableLabelSchemaContext;
	}
): Array<Record<string, unknown>> {
	return columns.map(col => {
		const columnId = String(col['id'] ?? '');
		const header = col['header'];
		const format = tableGridColumnHeaderFormat(col, undefined);
		if (typeof format !== 'string') {
			return col;
		}
		const normalized = normalizeTechnicalHeaderFormat(format, technical =>
			resolveTableColumnHeaderLabel({
				columnId,
				fieldName: context.fieldNameByColumnId?.get(columnId),
				cardLabel: context.headerLabelsById?.get(columnId),
				headerName: technical,
				schema: context.schema,
			})
		);
		if (normalized === format && typeof header === 'object') {
			return col;
		}
		return {
			...col,
			header: tableGridColumnHeaderOptions(normalized),
		};
	});
}

/** Fingerprint of grid column id order — used to re-init when columns are reordered. */
export function gridColumnsOrderFingerprint(
	columns: Array<Record<string, unknown>>
): string {
	return columns.map(col => String(col['id'] ?? '')).join('>');
}

/** Fingerprint of resolved grid header labels — used to re-init when labels change. */
export function gridColumnsHeaderFingerprint(
	columns: Array<Record<string, unknown>>
): string {
	return columns
		.map(col => {
			const id = String(col['id'] ?? '');
			const format = tableGridColumnHeaderFormat(col);
			return `${id}:${format}`;
		})
		.join('|');
}

/** Fingerprint of resolved grid cell renderers — used to re-init when cells format changes. */
export function gridColumnsCellsFingerprint(
	columns: Array<Record<string, unknown>>
): string {
	return columns
		.map(col => {
			const id = String(col['id'] ?? '');
			const cells = col['cells'];
			if (!cells || typeof cells !== 'object' || Array.isArray(cells)) {
				return `${id}:default`;
			}
			const record = cells as Record<string, unknown>;
			if (record['lineBreaks'] === true) {
				return `${id}:lineBreaks`;
			}
			if (typeof record['format'] === 'string') {
				return `${id}:format:${record['format']}`;
			}
			const renderer = record['renderer'];
			if (
				renderer &&
				typeof renderer === 'object' &&
				!Array.isArray(renderer)
			) {
				const type = (renderer as Record<string, unknown>)['type'];
				return `${id}:renderer:${typeof type === 'string' ? type : 'object'}`;
			}
			if (typeof record['formatter'] === 'function') {
				return `${id}:formatter`;
			}
			return `${id}:cells`;
		})
		.join('|');
}
